/**
 * NPS dialog (T211).
 *
 * Renders a 0-10 score picker + optional comment box. Submitting
 * posts via T203's startNpsFeedback helper → lands as a
 * FeedbackThread row with nps_score populated.
 *
 * Two-step UI:
 *   1. Pick a score 0-10 (one row of buttons, traffic-light coloured)
 *   2. Optional follow-up comment (auto-prompted text depends on score
 *      band: detractor 0-6 / passive 7-8 / promoter 9-10)
 *   3. Submit → close
 *
 * Keep the dialog non-blocking — render an X to dismiss without
 * answering; that path still marks the milestone done so we don't
 * re-prompt on the next dashboard mount.
 */
import { useState } from "react";
import { startNpsFeedback } from "../../api/feedback.api";


const PROMPT_BY_MILESTONE = {
  week_2:  "How likely are you to recommend ZenCricketData to a friend or colleague?",
  month_2: "How likely are you to keep using ZenCricketData and recommend it to others?",
};


const COMMENT_PLACEHOLDER_BY_BAND = {
  detractor: "What's the main thing that's not working for you?",
  passive:   "What would push this from \"good\" to \"great\" for you?",
  promoter:  "What do you love most? (Helps us keep doing it right)",
};


function scoreBand(score) {
  if (score <= 6) return "detractor";
  if (score <= 8) return "passive";
  return "promoter";
}


export default function NpsDialog({ milestone, onClose, onSubmitted }) {
  const [score, setScore] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!milestone) return null;

  const handleSubmit = async () => {
    if (score == null || busy) return;
    setBusy(true);
    setError("");
    try {
      await startNpsFeedback(score, comment, window.location.pathname);
      onSubmitted?.();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Could not send. Try again.");
      setBusy(false);
    }
  };

  const band = score == null ? null : scoreBand(score);

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div
        className="bg-bg-primary border border-border-default rounded-xl shadow-2xl max-w-md w-full p-5 animate-fade-in"
        role="dialog"
        aria-labelledby="nps-title"
      >
        <div className="flex items-start justify-between mb-3 gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-purple-300 font-semibold">
              Quick feedback
            </p>
            <h2 id="nps-title" className="text-lg font-bold text-text-primary mt-1">
              {PROMPT_BY_MILESTONE[milestone] || PROMPT_BY_MILESTONE.week_2}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary text-sm leading-none"
            aria-label="Dismiss"
          >×</button>
        </div>

        {/* 0-10 score row. Colours: 0-6 red (detractor), 7-8 amber
            (passive), 9-10 emerald (promoter). Selected ring is the
            band's colour. */}
        <div className="grid grid-cols-11 gap-1 mb-3">
          {Array.from({ length: 11 }, (_, n) => {
            const selected = score === n;
            const b = scoreBand(n);
            const baseClass = b === "detractor"
              ? "border-rose-500/40 hover:bg-rose-500/15"
              : b === "passive"
                ? "border-amber-500/40 hover:bg-amber-500/15"
                : "border-emerald-500/40 hover:bg-emerald-500/15";
            const selectedClass = selected
              ? (b === "detractor" ? "bg-rose-500 text-bg-main border-rose-400"
                : b === "passive" ? "bg-amber-500 text-bg-main border-amber-400"
                : "bg-emerald-500 text-bg-main border-emerald-400")
              : "bg-bg-secondary text-text-primary";
            return (
              <button
                key={n}
                onClick={() => setScore(n)}
                disabled={busy}
                className={`aspect-square rounded text-sm font-semibold border ${baseClass} ${selectedClass} transition`}
              >
                {n}
              </button>
            );
          })}
        </div>
        <div className="flex justify-between text-[10px] text-text-secondary mb-3">
          <span>Not at all likely</span>
          <span>Extremely likely</span>
        </div>

        {score != null && (
          <>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={COMMENT_PLACEHOLDER_BY_BAND[band]}
              maxLength={500}
              rows={3}
              disabled={busy}
              className="w-full bg-bg-secondary border border-border-default rounded-md p-2 text-sm text-text-primary resize-none"
            />
            <div className="text-[10px] text-text-secondary text-right mt-0.5">
              {comment.length} / 500 · optional
            </div>
          </>
        )}

        {error && (
          <div className="mt-2 text-xs text-rose-300">{error}</div>
        )}

        <div className="flex items-center justify-end gap-2 mt-3">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-3 py-1.5 rounded-md text-text-secondary hover:text-text-primary text-xs"
          >
            Maybe later
          </button>
          <button
            onClick={handleSubmit}
            disabled={score == null || busy}
            className="px-4 py-1.5 rounded-md bg-purple-600 text-white text-sm font-semibold hover:bg-purple-500 disabled:opacity-40"
          >
            {busy ? "Sending…" : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
}
